import { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { deleteRule, getAllPaginated1 } from "../../../../services/rules";
import {
  setReportsData,
  setReportsFilters,
  setReportsPage,
  setReportsSort,
  setReportsTake,
  setReportsTotalCount,
  useSelectFiltersReports,
  useSelectPageReports,
  useSelectSortReports,
  useSelectTakeReports,
  useSelectTotalCountReports,
} from "../../../../redux/features/customReport/customReportsSlice";
import { errorMsg, SORT_DIRECTION } from "../../../../utils/vars";

const useCustomReportsTable = () => {
  const dispatch = useDispatch();
  const sort = useSelectSortReports();
  const page = useSelectPageReports();
  const take = useSelectTakeReports();
  const totalCount = useSelectTotalCountReports();
  const filters = useSelectFiltersReports();

  const queryParams = useMemo(() => {
    const searchParams = new URLSearchParams();

    searchParams.append("page", page);
    searchParams.append("size", take);
    searchParams.append("direction", sort.direction);
    searchParams.append("type", sort.type);
    searchParams.append("rType", "ADVANCED_SEARCH_REPORT");

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        searchParams.append(key, value);
      }
    });

    return searchParams.toString();
  }, [filters, page, sort.direction, sort.type, take]);

  const {
    data,
    refetch: refetchReports,
    error: errorReportsFetch,
    isFetching: isFetchingReports,
  } = useQuery({
    queryKey: ["custom-reports", queryParams],
    queryFn: () => getAllPaginated1(queryParams),
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const deleteReportMutation = useMutation({
    mutationFn: deleteRule,
    onSuccess: async () => {
      await refetchReports();
      toast.success("Successfully deleted report");
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || errorMsg);
    },
  });

  useEffect(() => {
    if (data) {
      dispatch(setReportsData(data?.data));
      dispatch(setReportsTotalCount(data?.totalCount));
    }
  }, [data, dispatch]);

  const toggleSort = (type) => {
    const currentType = sort.type;
    const currentDirection = sort.direction;

    if (type === currentType) {
      const direction =
        SORT_DIRECTION.DESC === currentDirection
          ? SORT_DIRECTION.ASC
          : SORT_DIRECTION.DESC;
      dispatch(setReportsSort({ direction, type }));
    } else {
      dispatch(setReportsSort({ direction: SORT_DIRECTION.DESC, type }));
    }

    dispatch(setReportsPage(0));
  };

  const handleFilterChange = (key, filter) => {
    if (key !== "DATE") {
      dispatch(setReportsFilters({ [key]: filter }));
    } else {
      dispatch(setReportsFilters({ ...filter }));
    }

    dispatch(setReportsPage(0));
  };

  const handlePageChange = (newPage) => {
    dispatch(setReportsPage(newPage - 1));
  };

  const handleTakeChange = (newSize) => {
    dispatch(setReportsTake(newSize));
  };

  const deleteHandler = async (id) => {
    deleteReportMutation.mutate(id);
  };

  return {
    handleFilterChange,
    toggleSort,
    deleteHandler,
    handlePageChange,
    handleTakeChange,
    refetchReports,
    isFetchingReports,
    errorReportsFetch,
    sort,
    filters,
    take,
    page,
    totalCount,
  };
};
export default useCustomReportsTable;
